// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

// Shows a live uptime status dot at the top of the sidebar.
// Reads the last uptime_check row — green = up, red = down, yellow = slow.
// Falls back gracefully if the uptime_checks table doesn't exist yet.

import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

type Status = 'up' | 'down' | 'slow' | 'unknown'

const STATUS_STYLE: Record<Status, { color: string; glow: string; label: string }> = {
  up: { color: '#22c55e', glow: '#22c55e', label: 'guardrok.com is up' },
  slow: { color: '#f59e0b', glow: '#f59e0b', label: 'guardrok.com is slow' },
  down: { color: '#ef4444', glow: '#ef4444', label: 'guardrok.com is DOWN' },
  unknown: { color: '#374151', glow: 'transparent', label: 'Uptime monitoring inactive' },
}

export default function UptimeDot() {
  const [status, setStatus] = useState<Status>('unknown')
  const [ms, setMs] = useState<number | null>(null)

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('uptime_checks')
        .select('status, response_ms')
        .order('checked_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (data) {
        setStatus(data.status as Status)
        setMs(data.response_ms)
      }
    }
    load()

    // Refresh every 5 minutes to match cron cadence
    const t = setInterval(load, 5 * 60 * 1000)
    return () => clearInterval(t)
  }, [])

  const s = STATUS_STYLE[status]

  return (
    <div
      title={s.label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '4px 2px',
      }}
    >
      <div
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: s.color,
          boxShadow: status !== 'unknown' ? `0 0 6px ${s.glow}` : 'none',
          flexShrink: 0,
          animation: status === 'up' ? 'pulse 2s ease-in-out infinite' : 'none',
        }}
      />
      <span style={{ fontSize: 10, color: '#374151', letterSpacing: '0.3px' }}>
        {status === 'unknown'
          ? 'uptime monitoring off'
          : status === 'up'
            ? `up${ms ? ` · ${ms}ms` : ''}`
            : status === 'slow'
              ? `slow · ${ms}ms`
              : 'DOWN'}
      </span>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
      `}</style>
    </div>
  )
}
